import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL, fetchJson } from './api';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    Link,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import ConstructionOutlinedIcon from '@mui/icons-material/ConstructionOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

function getUserFacingLoginError(errorValue, fallback) {
    const raw = String(errorValue?.message || fallback || '').trim();
    const withoutApiDetail = raw.replace(/\s*\(API:\s*[^)]*\)\s*$/i, '').trim();
    const normalized = withoutApiDetail.toLowerCase();

    if (!withoutApiDetail) return fallback;
    if (normalized === 'username is required' || normalized === 'email is required') {
        return 'Please enter your email address.';
    }
    if (normalized === 'password is required') {
        return 'Please enter your password.';
    }
    if (normalized === 'login failed' || normalized === 'invalid username or password') {
        return 'The email address or password is incorrect.';
    }
    if (normalized === 'invalid email or password') {
        return 'The email address or password is incorrect.';
    }
    if (normalized === 'invalid code') {
        return 'That verification code is not correct. Check your authenticator app and try again.';
    }
    if (normalized === 'mfa verification failed') {
        return 'We could not verify that code. Check your authenticator app and try again.';
    }
    if (normalized === 'missing login information') {
        return 'We could not complete sign-in. Please try again.';
    }
    if (normalized.includes('table missing') || normalized.includes('migration')) {
        return 'Sign-in is temporarily unavailable. Please try again later.';
    }
    return withoutApiDetail;
}

function PasswordVisibilityButton({ visible, onClick }) {
    return (
        <Tooltip title={visible ? 'Hide password' : 'Show password'}>
            <IconButton
                aria-label={visible ? 'Hide password' : 'Show password'}
                onClick={onClick}
                edge="end"
                size="small"
            >
                {visible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
            </IconButton>
        </Tooltip>
    );
}

function getBrokerPortalUrl() {
    const configured = String(process.env.REACT_APP_CLIENTDEAL_PORTAL_URL || '').trim();
    if (configured) return configured;
    try {
        if (String(window.location.hostname || '').toLowerCase().endsWith('.co.uk')) {
            return 'https://clientdeal.monsurv.co.uk';
        }
    } catch { }
    return 'https://clientdeal.monsurv.com';
}

export default function LoginPage({ onLogin, onMfaComplete, onMfaCancel, mfaRequired, mfaUser, error, loading }) {
    const [active, setActive] = useState('menu');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [otp, setOtp] = useState('');
    const [trustDevice, setTrustDevice] = useState(false);

    const [devError, setDevError] = useState('');
    const [devLoading, setDevLoading] = useState(false);
    const [devLoginEmail, setDevLoginEmail] = useState('');
    const [devLoginPassword, setDevLoginPassword] = useState('');
    const [showDeveloperPassword, setShowDeveloperPassword] = useState(false);

    const [forgotIdentifier, setForgotIdentifier] = useState('');
    const [forgotMessage, setForgotMessage] = useState('');
    const [forgotError, setForgotError] = useState('');
    const [forgotLoading, setForgotLoading] = useState(false);

    const [resetUsername, setResetUsername] = useState('');
    const [resetToken, setResetToken] = useState('');
    const [resetNewPassword, setResetNewPassword] = useState('');
    const [resetConfirmNewPassword, setResetConfirmNewPassword] = useState('');
    const [showResetPassword, setShowResetPassword] = useState(false);
    const [showResetConfirmation, setShowResetConfirmation] = useState(false);
    const [resetMessage, setResetMessage] = useState('');
    const [resetError, setResetError] = useState('');
    const [resetLoading, setResetLoading] = useState(false);

    const panelMeta = useMemo(() => {
        if (active === 'menu') {
            return {
                eyebrow: 'Secure Access',
                title: 'Log In',
                subtitle: 'Choose the workspace you want to enter and continue through the relevant authentication flow.'
            };
        }
        if (active === 'surveyor' && !mfaRequired) {
            return {
                eyebrow: 'Surveyor Access',
                title: 'Dashboard Sign In',
                subtitle: 'Enter your Monsurv dashboard credentials to continue into the operational workspace.'
            };
        }
        if (active === 'surveyor' && mfaRequired) {
            return {
                eyebrow: 'Verification',
                title: 'Two-Factor Check',
                subtitle: 'Confirm the six-digit authenticator code to complete sign-in securely.'
            };
        }
        if (active === 'forgot') {
            return {
                eyebrow: 'Recovery',
                title: 'Password Recovery',
                subtitle: 'Request a reset link using your account email or username.'
            };
        }
        if (active === 'reset') {
            return {
                eyebrow: 'Credentials',
                title: 'Reset Password',
                subtitle: 'Set a new password and return to the surveyor workspace.'
            };
        }
        return {
            eyebrow: 'Developer Access',
            title: 'Developer Sign In',
            subtitle: 'Use your developer email and password to enter the client-side project portal.'
        };
    }, [active, mfaRequired]);

    const glassPanelSx = {
        width: '100%',
        maxWidth: { md: 470 },
        minWidth: 0,
        p: { xs: 2.25, sm: 3 },
        display: 'flex',
        flexDirection: 'column',
        minHeight: { md: 'calc((var(--app-vh, 1vh) * 100) - 128px)' },
        borderRadius: 5,
        background: `linear-gradient(180deg, ${alpha('#ffffff', 0.94)} 0%, ${alpha('#f7fbff', 0.88)} 100%)`,
        border: `1px solid ${alpha('#ffffff', 0.72)}`,
        boxShadow: '0 30px 60px rgba(15, 23, 42, 0.12)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)'
    };

    const actionTileSx = (accent, disabled = false) => ({
        justifyContent: 'flex-start',
        textAlign: 'left',
        py: 1.55,
        px: 1.5,
        borderRadius: 1.75,
        border: `1px solid ${disabled ? alpha('#94a3b8', 0.2) : alpha(accent, 0.16)}`,
        background: disabled
            ? `linear-gradient(180deg, ${alpha('#f8fafc', 0.84)} 0%, ${alpha('#eef2f7', 0.82)} 100%)`
            : `linear-gradient(180deg, ${alpha('#ffffff', 0.96)} 0%, ${alpha(accent, 0.08)} 100%)`,
        boxShadow: disabled ? 'none' : '0 16px 28px rgba(15, 23, 42, 0.08)',
        color: disabled ? 'text.secondary' : 'text.primary',
        '&:hover': disabled ? {} : {
            borderColor: alpha(accent, 0.3),
            background: `linear-gradient(180deg, ${alpha('#ffffff', 0.98)} 0%, ${alpha(accent, 0.12)} 100%)`
        }
    });

    const formPanelSx = {
        mt: 2.25,
        p: { xs: 1.4, sm: 1.7 },
        borderRadius: 4,
        border: `1px solid ${alpha('#ffffff', 0.64)}`,
        background: `linear-gradient(180deg, ${alpha('#ffffff', 0.8)} 0%, ${alpha('#f6faff', 0.72)} 100%)`,
        boxShadow: '0 18px 34px rgba(15, 23, 42, 0.08)'
    };

    const heroCardSx = {
        p: { xs: 2.1, md: 3.2 },
        width: '100%',
        minWidth: 0,
        height: 'auto',
        minHeight: { md: 320 },
        borderRadius: 5,
        border: `1px solid ${alpha('#ffffff', 0.12)}`,
        background: [
            `radial-gradient(circle at 18% 16%, ${alpha('#60a5fa', 0.18)} 0%, transparent 28%)`,
            `radial-gradient(circle at 84% 22%, ${alpha('#22c55e', 0.12)} 0%, transparent 24%)`,
            `linear-gradient(155deg, ${alpha('#08111f', 0.98)} 0%, ${alpha('#0f172a', 0.96)} 48%, ${alpha('#162033', 0.94)} 100%)`
        ].join(', '),
        boxShadow: '0 34px 70px rgba(2, 6, 23, 0.3)',
        color: '#e5eef9',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.06) 1px, transparent 1px)',
            backgroundSize: '42px 42px',
            opacity: 0.28,
            maskImage: 'linear-gradient(180deg, rgba(0,0,0,0.68), transparent 82%)',
            pointerEvents: 'none'
        }
    };

    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search || '');
            const user = String(params.get('reset_username') || '').trim();
            const token = String(params.get('reset_token') || '').trim();
            if (user && token) {
                setActive('reset');
                setResetUsername(user);
                setResetToken(token);
            }
        } catch {
            // ignore
        }
    }, []);

    useEffect(() => {
        if (active !== 'developer') return;
        setDevError('');
        setDevLoading(false);
        setDevLoginEmail('');
        setDevLoginPassword('');
        setShowDeveloperPassword(false);
    }, [active]);

    useEffect(() => {
        if (!mfaRequired) {
            setTrustDevice(false);
        }
    }, [mfaRequired]);

    async function submit(event) {
        event.preventDefault();
        if (!username || !password) return;
        await onLogin({ username, password });
    }

    async function submitMfa(event) {
        event.preventDefault();
        if (!onMfaComplete) return;
        const code = String(otp || '').trim();
        if (!code) return;
        await onMfaComplete({ code, trustDevice });
    }

    function completeDeveloperLogin({ token, reference }) {
        const ref = String(reference || '').trim();
        if (!token || !ref) throw new Error('Missing login information');
        try { localStorage.setItem('monsurv_portal_auth_token', token); } catch { }
        try { localStorage.setItem('monsurv_portal_auth_mode', 'developer'); } catch { }
        const returnTo = encodeURIComponent('/');
        window.location.assign(`/?portal=developer&reference=${encodeURIComponent(ref)}&view=overview&returnTo=${returnTo}`);
    }

    async function submitDeveloperLogin(event) {
        event.preventDefault();
        const email = String(devLoginEmail || '').trim();
        const nextPassword = String(devLoginPassword || '');
        if (!email || !nextPassword) return;

        setDevError('');
        setDevLoading(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/portal/developer/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password: nextPassword })
            });

            completeDeveloperLogin({ token: json?.token, reference: json?.reference });
        } catch (errorValue) {
            setDevError(getUserFacingLoginError(errorValue, 'We could not complete sign-in. Please try again.'));
        } finally {
            setDevLoading(false);
        }
    }

    async function submitForgotPassword(event) {
        event.preventDefault();
        setForgotError('');
        setForgotMessage('');
        setForgotLoading(true);
        try {
            const identifier = String(forgotIdentifier || '').trim();
            if (!identifier) throw new Error('Enter your email or username');

            const result = await fetchJson(`${API_BASE_URL}/auth/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: identifier })
            });

            if (result?.rateLimited) {
                setForgotError('Too many reset requests. Please wait a few minutes and try again.');
            } else if (result?.accountExists === false) {
                setForgotError('We could not find an account matching that email or username.');
            } else if (result?.accountExists === true && result?.resetEmailSent === true) {
                setForgotMessage('A password reset link has been sent to your email address.');
            } else if (result?.accountExists === true) {
                setForgotError('We found your account, but could not send a reset email. Please contact support.');
            } else {
                setForgotError('We could not check your account right now. Please try again later.');
            }
        } catch (errorValue) {
            setForgotError(getUserFacingLoginError(errorValue, 'We could not check your account right now. Please try again later.'));
        } finally {
            setForgotLoading(false);
        }
    }

    async function submitResetPassword(event) {
        event.preventDefault();
        setResetError('');
        setResetMessage('');
        setResetLoading(true);
        try {
            const user = String(resetUsername || '').trim();
            const token = String(resetToken || '').trim();
            const passwordOne = String(resetNewPassword || '');
            const passwordTwo = String(resetConfirmNewPassword || '');

            if (!user || !token) throw new Error('Reset link is missing required information');
            if (!passwordOne) throw new Error('Enter a new password');
            if (passwordOne !== passwordTwo) throw new Error('Passwords do not match');

            await fetchJson(`${API_BASE_URL}/auth/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: user, token, newPassword: passwordOne, confirmNewPassword: passwordTwo })
            });

            setResetMessage('Password updated. You can now sign in with your new password.');

            try {
                const url = new URL(window.location.href);
                url.searchParams.delete('reset_username');
                url.searchParams.delete('reset_token');
                window.history.replaceState({}, '', url.toString());
            } catch {
                // ignore
            }
        } catch (errorValue) {
            setResetError(getUserFacingLoginError(errorValue, 'We could not reset your password. Please try again.'));
        } finally {
            setResetLoading(false);
        }
    }

    return (
        <Grid
            container
            spacing={2}
            className="login-shell"
            sx={{
                position: 'relative',
                width: 'calc(100% + 40px)',
                margin: '-20px',
                padding: '20px',
                boxSizing: 'border-box',
                minHeight: { xs: 'calc((var(--app-vh, 1vh) * 100) - 128px)', md: 'calc((var(--app-vh, 1vh) * 100) - 128px)' },
                alignItems: 'stretch',
                flexWrap: { xs: 'wrap', md: 'nowrap' }
            }}
        >
            <Grid
                size={{ xs: 12, md: 'auto' }}
                sx={{
                    flex: { md: '0 1 470px' },
                    minWidth: { md: 360 },
                    maxWidth: { md: 470 }
                }}
            >
                <Paper sx={glassPanelSx}>
                    <Stack spacing={1.2}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', alignSelf: 'flex-start', px: 1.15, py: 0.65, borderRadius: 999, background: alpha('#243242', 0.08), color: '#243242', fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                            Monsurv
                        </Box>
                        <Box>
                            <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'text.secondary' }}>
                                {panelMeta.eyebrow}
                            </Typography>
                            <Typography variant="h4" sx={{ mt: 0.6, fontWeight: 900, letterSpacing: '-0.04em' }}>
                                {panelMeta.title}
                            </Typography>
                        </Box>
                    </Stack>

                    {active === 'menu' ? (
                        <Stack sx={{ mt: 2.4 }} spacing={1.6}>
                            <Button fullWidth onClick={() => setActive('surveyor')} sx={actionTileSx('#1d4ed8')}>
                                <Stack direction="row" spacing={1.4} alignItems="flex-start" sx={{ width: '100%' }}>
                                    <Box sx={{ width: 42, height: 42, borderRadius: 1.25, display: 'grid', placeItems: 'center', background: alpha('#1d4ed8', 0.12), color: '#1d4ed8', flexShrink: 0 }}>
                                        <DashboardCustomizeOutlinedIcon fontSize="small" />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontWeight: 800, lineHeight: 1.1 }}>Surveyor Login</Typography>
                                        <Typography variant="body2" sx={{ mt: 0.45, color: 'text.secondary' }}>
                                            Access reporting operations, deadlines, project data and the dashboard workspace.
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Button>

                            <Button fullWidth onClick={() => setActive('developer')} sx={actionTileSx('#7c3aed')}>
                                <Stack direction="row" spacing={1.4} alignItems="flex-start" sx={{ width: '100%' }}>
                                    <Box sx={{ width: 42, height: 42, borderRadius: 1.25, display: 'grid', placeItems: 'center', background: alpha('#7c3aed', 0.12), color: '#7c3aed', flexShrink: 0 }}>
                                        <ConstructionOutlinedIcon fontSize="small" />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontWeight: 800, lineHeight: 1.1 }}>Developer Login</Typography>
                                        <Typography variant="body2" sx={{ mt: 0.45, color: 'text.secondary' }}>
                                            Enter the client-side portal to review projects, reporting and funding-related progress.
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Button>

                            <Button fullWidth component="a" href={getBrokerPortalUrl()} sx={actionTileSx('#047857')}>
                                <Stack direction="row" spacing={1.4} alignItems="flex-start" sx={{ width: '100%' }}>
                                    <Box sx={{ width: 42, height: 42, borderRadius: 1.25, display: 'grid', placeItems: 'center', background: alpha('#047857', 0.12), color: '#047857', flexShrink: 0 }}>
                                        <BusinessCenterOutlinedIcon fontSize="small" />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontWeight: 800, lineHeight: 1.1 }}>Broker Login</Typography>
                                        <Typography variant="body2" sx={{ mt: 0.45, color: 'text.secondary' }}>
                                            Enter the Broker portal to submit and track Feasibility Analysis requests.
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Button>

                            <Button fullWidth disabled sx={actionTileSx('#94a3b8', true)}>
                                <Stack direction="row" spacing={1.4} alignItems="flex-start" sx={{ width: '100%' }}>
                                    <Box sx={{ width: 42, height: 42, borderRadius: 1.25, display: 'grid', placeItems: 'center', background: alpha('#94a3b8', 0.16), color: 'text.secondary', flexShrink: 0 }}>
                                        <AccountBalanceOutlinedIcon fontSize="small" />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                            <Typography sx={{ fontWeight: 800, lineHeight: 1.1 }}>Funder / Investor Login</Typography>
                                            <Box sx={{ px: 0.9, py: 0.35, borderRadius: 999, background: alpha('#94a3b8', 0.14), color: 'text.secondary', fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>
                                                Coming Soon
                                            </Box>
                                        </Stack>
                                        <Typography variant="body2" sx={{ mt: 0.45, color: 'text.secondary' }}>
                                            Reserved for client-side and funding partner access in a later release.
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Button>

                        </Stack>
                    ) : active === 'surveyor' ? (
                        <Box sx={formPanelSx}>
                            <Button startIcon={<ArrowBackIcon />} onClick={() => setActive('menu')} sx={{ px: 0, fontWeight: 700 }}>
                                Back
                            </Button>

                            {!mfaRequired ? (
                                <>
                                    <Typography variant="h6" sx={{ mt: 1.1, fontWeight: 900 }}>
                                        Surveyor Login
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 0.6, color: 'text.secondary' }}>
                                        Enter your dashboard credentials.
                                    </Typography>

                                    <Box component="form" onSubmit={submit} sx={{ mt: 2.1 }}>
                                        <Stack spacing={1.4}>
                                            <TextField label="Username" value={username} onChange={(event) => setUsername(event.target.value)} placeholder="Enter username" autoComplete="username" size="small" fullWidth />
                                            <TextField
                                                label="Password"
                                                type={showPassword ? 'text' : 'password'}
                                                value={password}
                                                onChange={(event) => setPassword(event.target.value)}
                                                placeholder="Password"
                                                autoComplete="current-password"
                                                size="small"
                                                fullWidth
                                                InputProps={{
                                                    endAdornment: <InputAdornment position="end"><PasswordVisibilityButton visible={showPassword} onClick={() => setShowPassword((visible) => !visible)} /></InputAdornment>
                                                }}
                                            />
                                            {error ? <Alert severity="error">{error}</Alert> : null}
                                            <Button type="submit" fullWidth variant="contained" disabled={loading || !username || !password} sx={{ py: 1.15, fontWeight: 800 }}>
                                                {loading ? 'Signing in…' : 'Sign In'}
                                            </Button>
                                            <Button
                                                type="button"
                                                onClick={() => {
                                                    setForgotIdentifier(String(username || '').trim());
                                                    setForgotError('');
                                                    setForgotMessage('');
                                                    setActive('forgot');
                                                }}
                                                disabled={loading}
                                                sx={{ px: 0, justifyContent: 'flex-start', fontWeight: 700 }}
                                            >
                                                Forgot password?
                                            </Button>
                                        </Stack>
                                    </Box>
                                </>
                            ) : (
                                <>
                                    <Typography variant="h6" sx={{ mt: 1.1, fontWeight: 900 }}>
                                        Two-Factor Verification
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 0.6, color: 'text.secondary' }}>
                                        Enter the 6-digit code from your authenticator app{mfaUser?.username ? ` for ${mfaUser.username}` : ''}.
                                    </Typography>

                                    <Box component="form" onSubmit={submitMfa} sx={{ mt: 2.1 }}>
                                        <Stack spacing={1.4}>
                                            <TextField label="Authentication Code" value={otp} onChange={(event) => setOtp(event.target.value)} placeholder="123456" inputMode="numeric" autoComplete="one-time-code" size="small" fullWidth />
                                            <FormControlLabel
                                                control={<Checkbox checked={trustDevice} onChange={(event) => setTrustDevice(event.target.checked)} />}
                                                label="Trust this Device for 30 Days"
                                            />
                                            {error ? <Alert severity="error">{error}</Alert> : null}
                                            <Button type="submit" fullWidth variant="contained" disabled={loading || !String(otp || '').trim()} sx={{ py: 1.15, fontWeight: 800 }}>
                                                {loading ? 'Verifying…' : 'Verify'}
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="outlined"
                                                onClick={() => {
                                                    setOtp('');
                                                    setTrustDevice(false);
                                                    if (onMfaCancel) onMfaCancel();
                                                }}
                                                disabled={loading}
                                                sx={{ fontWeight: 700 }}>
                                                Cancel
                                            </Button>
                                        </Stack>
                                    </Box>
                                </>
                            )}
                        </Box>
                    ) : active === 'forgot' ? (
                        <Box sx={formPanelSx}>
                            <Button startIcon={<ArrowBackIcon />} onClick={() => setActive('surveyor')} sx={{ px: 0, fontWeight: 700 }}>
                                Back
                            </Button>

                            <Typography variant="h6" sx={{ mt: 1.1, fontWeight: 900 }}>
                                Forgot Password
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.6, color: 'text.secondary' }}>
                                Enter your account email or username. If an account exists, we will email you a reset link.
                            </Typography>

                            <Box component="form" onSubmit={submitForgotPassword} sx={{ mt: 2.1 }}>
                                <Stack spacing={1.4}>
                                    <TextField label="Email or Username" value={forgotIdentifier} onChange={(event) => setForgotIdentifier(event.target.value)} placeholder="name@domain.com" size="small" fullWidth />
                                    {forgotError ? <Alert severity="error">{forgotError}</Alert> : null}
                                    {forgotMessage ? <Alert severity="success">{forgotMessage}</Alert> : null}
                                    <Button type="submit" fullWidth variant="contained" disabled={forgotLoading || !String(forgotIdentifier || '').trim()} sx={{ py: 1.15, fontWeight: 800 }}>
                                        {forgotLoading ? 'Sending…' : 'Send Reset Email'}
                                    </Button>
                                </Stack>
                            </Box>
                        </Box>
                    ) : active === 'reset' ? (
                        <Box sx={formPanelSx}>
                            <Button startIcon={<ArrowBackIcon />} onClick={() => setActive('surveyor')} sx={{ px: 0, fontWeight: 700 }}>
                                Back
                            </Button>

                            <Typography variant="h6" sx={{ mt: 1.1, fontWeight: 900 }}>
                                Reset Password
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.6, color: 'text.secondary' }}>
                                Set a new password for {resetUsername ? resetUsername : 'your account'}.
                            </Typography>

                            <Box component="form" onSubmit={submitResetPassword} sx={{ mt: 2.1 }}>
                                <Stack spacing={1.4}>
                                    <TextField
                                        label="New Password"
                                        type={showResetPassword ? 'text' : 'password'}
                                        value={resetNewPassword}
                                        onChange={(event) => setResetNewPassword(event.target.value)}
                                        placeholder="New password"
                                        size="small"
                                        fullWidth
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end"><PasswordVisibilityButton visible={showResetPassword} onClick={() => setShowResetPassword((visible) => !visible)} /></InputAdornment>
                                        }}
                                    />
                                    <TextField
                                        label="Confirm New Password"
                                        type={showResetConfirmation ? 'text' : 'password'}
                                        value={resetConfirmNewPassword}
                                        onChange={(event) => setResetConfirmNewPassword(event.target.value)}
                                        placeholder="Confirm new password"
                                        size="small"
                                        fullWidth
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end"><PasswordVisibilityButton visible={showResetConfirmation} onClick={() => setShowResetConfirmation((visible) => !visible)} /></InputAdornment>
                                        }}
                                    />
                                    {resetError ? <Alert severity="error">{resetError}</Alert> : null}
                                    {resetMessage ? <Alert severity="success">{resetMessage}</Alert> : null}
                                    <Button type="submit" fullWidth variant="contained" disabled={resetLoading || !resetNewPassword || !resetConfirmNewPassword} sx={{ py: 1.15, fontWeight: 800 }}>
                                        {resetLoading ? 'Saving…' : 'Reset Password'}
                                    </Button>
                                </Stack>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={formPanelSx}>
                            <Button startIcon={<ArrowBackIcon />} onClick={() => setActive('menu')} sx={{ px: 0, fontWeight: 700 }}>
                                Back
                            </Button>

                            <Typography variant="h6" sx={{ mt: 1.1, fontWeight: 900 }}>
                                Developer Login
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.6, color: 'text.secondary' }}>
                                Sign in with your developer email and password.
                            </Typography>

                            <Box component="form" onSubmit={submitDeveloperLogin} sx={{ mt: 2.1 }}>
                                <Stack spacing={1.4}>
                                    <TextField label="Developer Email" value={devLoginEmail} onChange={(event) => setDevLoginEmail(event.target.value)} placeholder="name@domain.com" size="small" fullWidth />
                                    <TextField
                                        label="Your Password"
                                        type={showDeveloperPassword ? 'text' : 'password'}
                                        value={devLoginPassword}
                                        onChange={(event) => setDevLoginPassword(event.target.value)}
                                        placeholder="Password"
                                        size="small"
                                        fullWidth
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end"><PasswordVisibilityButton visible={showDeveloperPassword} onClick={() => setShowDeveloperPassword((visible) => !visible)} /></InputAdornment>
                                        }}
                                    />
                                    {devError ? <Alert severity="error">{devError}</Alert> : null}
                                    <Button type="submit" fullWidth variant="contained" disabled={devLoading || !devLoginEmail.trim() || !devLoginPassword} sx={{ py: 1.15, fontWeight: 800 }}>
                                        {devLoading ? 'Signing in…' : 'Sign In'}
                                    </Button>
                                </Stack>
                            </Box>
                        </Box>
                    )}

                    <Box sx={{ flex: 1 }} />

                    <Stack spacing={1.1} sx={{ mt: 2.2 }}>
                        <Stack direction="row" justifyContent="space-between" sx={{ color: 'text.secondary' }}>
                            <Typography variant="caption">Monsurv-beta(internal)</Typography>
                            <Typography variant="caption">Monsurv Dashboard</Typography>
                        </Stack>
                        <Link href="/?view=privacy" variant="caption" underline="hover" sx={{ alignSelf: 'flex-start', color: 'text.secondary' }}>
                            Privacy Policy
                        </Link>
                        {process.env.NODE_ENV !== 'production' ? <Divider /> : null}
                    </Stack>
                </Paper>
            </Grid>

            <Grid
                size={{ xs: 12, md: 'grow' }}
                sx={{
                    display: { xs: 'none', md: 'block' },
                    flex: { md: '1 1 0' },
                    minWidth: 0,
                    paddingTop: '0 !important',
                    paddingLeft: '0 !important'
                }}
            >
                <Box className="login-hero" sx={{ ...heroCardSx, display: { xs: 'none', md: 'flex' }, width: '100%' }}>
                    <Box
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                            display: 'flex',
                            alignItems: 'flex-start',
                            height: 'auto',
                            width: '100%'
                        }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            <Typography
                                variant="h3"
                                sx={{
                                    mt: 2.4,
                                    fontWeight: 900,
                                    lineHeight: 1.02,
                                    letterSpacing: '-0.05em',
                                    maxWidth: 'none',
                                    whiteSpace: 'normal',
                                    overflowWrap: 'anywhere'
                                }}
                            >
                                Project Monitoring, Built for Clarity
                            </Typography>

                            <Typography variant="body1" sx={{ mt: 1.8, maxWidth: 820, color: alpha('#dbeafe', 0.82), lineHeight: 1.7 }}>
                                Monsurv is a project monitoring platform designed to streamline reporting, centralise project data, and provide clear oversight of risk, cost, progress, and compliance throughout the development lifecycle. Built for monitoring surveyors, funders, and project stakeholders, it brings live project information together to support faster decisions and more efficient monitoring.
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Grid>
        </Grid>
    );
}
